/**
 * Utilities for working with units of measure
 */

/**
 * Processes the product's units of measure string
 * @param {string} unitOfMeasure - Units string (e.g.: "kg" or "{kg,un,cx}")
 * @returns {string[]} Array of units
 */
export function parseUnitsOfMeasure(unitOfMeasure) {
  if (!unitOfMeasure || typeof unitOfMeasure !== 'string') {
    return []
  }

  // Removes whitespace
  const cleaned = unitOfMeasure.trim()

  // If it starts and ends with braces, it is multiple units
  if (cleaned.startsWith('{') && cleaned.endsWith('}')) {
    // Removes the braces and splits by comma
    const unitsString = cleaned.slice(1, -1)
    return unitsString.split(',').map(unit => unit.trim()).filter(unit => unit.length > 0)
  }

  // If it has no braces, it is a single unit
  return cleaned.length > 0 ? [cleaned] : []
}

/**
 * Checks if the product has multiple units of measure
 * @param {string} unitOfMeasure - Units string
 * @returns {boolean} True if it has multiple units
 */
export function hasMultipleUnits(unitOfMeasure) {
  const units = parseUnitsOfMeasure(unitOfMeasure)
  return units.length > 1
}

/**
 * Checks if the product has at least one unit of measure
 * @param {string} unitOfMeasure - Units string
 * @returns {boolean} True if it has at least one unit
 */
export function hasUnits(unitOfMeasure) {
  const units = parseUnitsOfMeasure(unitOfMeasure)
  return units.length > 0
}

/**
 * Formats the units for display
 * @param {string[]} units - Array of units
 * @returns {string} String formatted for display
 */
export function formatUnitsForDisplay(units) {
  if (!units || units.length === 0) {
    return ''
  }

  if (units.length === 1) {
    return units[0]
  }

  return units.join(', ')
}

/**
 * Gets the first (default) unit of the product
 * @param {string} unitOfMeasure - Units string
 * @returns {string|null} First unit or null if there is none
 */
export function getDefaultUnit(unitOfMeasure) {
  const units = parseUnitsOfMeasure(unitOfMeasure)
  return units.length > 0 ? units[0] : null
}
